import React, { useEffect, useState } from 'react';
import './WorldDbEmptyStart.css';

const ROTATING_WORDS = ['clients', 'leads', 'prospects'];
const WORD_HOLD_MS = 2000;
const WORD_TYPE_MS = 54;
const WORD_DELETE_MS = 36;

const IconUpload = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3v12m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconPerson = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
    <path d="M19 8v6M22 11h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconCode = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 9l-3 3 3 3M15 9l3 3-3 3M14 4l-4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WorldDbEmptyStart = ({
  onImportList,
  onAddContact,
  onConnectApi,
  canWrite = true,
}) => {
  const [typedWord, setTypedWord] = useState(ROTATING_WORDS[0]);
  const [caretOn, setCaretOn] = useState(false);

  useEffect(() => {
    const reduceMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let cancelled = false;
    let timeoutId = 0;
    const sleep = (ms) => new Promise((resolve) => {
      timeoutId = window.setTimeout(resolve, ms);
    });

    if (reduceMotion) {
      let i = 0;
      const id = window.setInterval(() => {
        i = (i + 1) % ROTATING_WORDS.length;
        setTypedWord(ROTATING_WORDS[i]);
      }, WORD_HOLD_MS);
      return () => window.clearInterval(id);
    }

    (async () => {
      let i = 0;
      setTypedWord(ROTATING_WORDS[0]);
      while (!cancelled) {
        setCaretOn(false);
        await sleep(WORD_HOLD_MS);
        if (cancelled) return;
        setCaretOn(true);
        const current = ROTATING_WORDS[i];
        for (let n = current.length; n >= 0; n -= 1) {
          if (cancelled) return;
          setTypedWord(current.slice(0, n));
          await sleep(WORD_DELETE_MS);
        }
        i = (i + 1) % ROTATING_WORDS.length;
        const next = ROTATING_WORDS[i];
        for (let n = 1; n <= next.length; n += 1) {
          if (cancelled) return;
          setTypedWord(next.slice(0, n));
          await sleep(WORD_TYPE_MS);
        }
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, []);

  return (
  <div className="wdEmpty wdEmpty--targets">
    <div className="wdEmpty-panel">
      <div className="wdEmpty-hero">
        <span className="wdEmpty-badge" aria-hidden="true">
          <IconUpload />
        </span>
        <p className="wdEmpty-eyebrow">Targets</p>
        <h2 className="wdEmpty-title">
          <span className="wdEmpty-titleGhost" aria-hidden="true">
            Import your <span className="wdEmpty-swap">prospects</span>
          </span>
          <span className="wdEmpty-titleLive">
            Import your{' '}
            <span className="wdEmpty-swap" aria-live="polite">
              {typedWord}
            </span>
            {caretOn && <span className="wdEmpty-caret" aria-hidden="true" />}
          </span>
        </h2>
        <p className="wdEmpty-lead">
          Nothing here yet. Upload a file, add one contact, or stream names from
          your own tools. Then outreach can start.
        </p>
      </div>

      <div className="wdEmpty-grid">
        <button
          type="button"
          className="wdEmpty-card wdEmpty-card--primary"
          onClick={onImportList}
          disabled={!canWrite}
        >
          <span className="wdEmpty-cardIcon wdEmpty-cardIcon--import">
            <IconUpload />
          </span>
          <span className="wdEmpty-cardBody">
            <span className="wdEmpty-cardTitle">Import list</span>
            <span className="wdEmpty-cardText">
              Upload a CSV or TXT. Name and Email are enough. We map the rest.
            </span>
          </span>
          <span className="altBtn altBtn--save wdEmpty-cardBtn">Start upload</span>
        </button>

        <button
          type="button"
          className="wdEmpty-card"
          onClick={onAddContact}
          disabled={!canWrite}
        >
          <span className="wdEmpty-cardIcon wdEmpty-cardIcon--create">
            <IconPerson />
          </span>
          <span className="wdEmpty-cardBody">
            <span className="wdEmpty-cardTitle">Add a contact</span>
            <span className="wdEmpty-cardText">
              Create one entry by hand. Ideal for a first test before a full import.
            </span>
          </span>
          <span className="altBtn altBtn--petrol wdEmpty-cardBtn">Add contact</span>
        </button>

        <button
          type="button"
          className="wdEmpty-card"
          onClick={onConnectApi}
        >
          <span className="wdEmpty-cardIcon wdEmpty-cardIcon--api">
            <IconCode />
          </span>
          <span className="wdEmpty-cardBody">
            <span className="wdEmpty-cardTitle">
              Connect via API
              <span className="wdEmpty-tag">Dev</span>
            </span>
            <span className="wdEmpty-cardText">
              Stream contacts from your stack. Keys and the field schema live in Settings.
            </span>
          </span>
          <span className="altBtn altBtn--petrol wdEmpty-cardBtn">Open API</span>
        </button>
      </div>

      {!canWrite && (
        <p className="wdEmpty-readonly">
          This workspace is read-only. Ask the owner to import the first list.
        </p>
      )}
    </div>
  </div>
  );
};

export default WorldDbEmptyStart;
